import {
  LESSON_TYPE_INSTRUCTIONAL,
  LESSON_TYPE_NONE_ASSESSMENT,
  TOPIC_POST_ASSESSMENT,
  TOPIC_PRE_ASSESSMENT,
  UNIT_SUMMATIVE_ASSESSMENT,
} from '../constants/LessonConstant';

export const NEUTRAL_TYPE_TAG_COLOR_KEY = 'neutral';

/**
 * Colour keys for labels that aren't in the design. Each key needs a matching pair in
 * $activity-type-tag-colors (LessonCard.scss), and none may repeat a design pair.
 */
export const EXTRA_TYPE_TAG_COLOR_KEYS = [
  'extra-green',
  'extra-red',
  'extra-pink',
  'extra-purple',
  'extra-lime',
  'extra-brown',
  'extra-slate',
  'extra-magenta',
];

const LESSON_TYPE_TAG_LABELS = {
  [TOPIC_PRE_ASSESSMENT]: 'Pre-assessment',
  [TOPIC_POST_ASSESSMENT]: 'Post-assessment',
  [UNIT_SUMMATIVE_ASSESSMENT]: 'Unit Assessment',
  [LESSON_TYPE_INSTRUCTIONAL]: 'Instructional Text',
};

const TYPE_TAG_COLOR_KEYS = {
  'pre-assessment': 'pre-assessment',
  'post-assessment': 'post-assessment',
  'post assessment': 'post-assessment',
  vocabulary: 'vocabulary',
  'instructional text': 'instructional-text',
  story: 'story',
  sequencing: 'sequencing',
  categorizing: 'categorizing',
  game: 'game',
};

const DETAILS_SEPARATOR = ' • ';

const TITLE_SEPARATOR = ' - ';

export const MULTI_ACTIVITY_LABEL = 'Multi-Activity';

// Page-type key the backend uses for pages that hold no playable activity
const UNSUPPORTED_PAGE_TYPE = 'Unsupported';

const trimToString = (value) => (value === null || value === undefined ? '' : String(value).trim());

const isUnitSummativeAssessment = (lessonType) => trimToString(lessonType).toUpperCase() === UNIT_SUMMATIVE_ASSESSMENT;

// Course activities are the lessons that carry course details (unit and topic)
const isCourseActivity = (attributes) => trimToString((attributes || {}).unitName) !== '';

// "7 - Author's Purpose" -> "Author's Purpose"
const stripLeadingNumber = (name) => trimToString(name).replace(/^\d+\s*-\s*/, '').replace(/\s+/g, ' ').trim();

const titleCase = (code) => code
  .split(/[_\s]+/)
  .filter((word) => word.length > 0)
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
  .join(' ');

const getLessonTypeLabel = (lessonType) => {
  const code = trimToString(lessonType);
  if (!code || code.toUpperCase() === LESSON_TYPE_NONE_ASSESSMENT) {
    return '';
  }
  return LESSON_TYPE_TAG_LABELS[code.toUpperCase()] || titleCase(code);
};

// 32-bit FNV-1a, so a label keeps its colour across cards, screens and reloads
const hashLabel = (text) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = Math.imul(hash ^ text.charCodeAt(i), 0x01000193) >>> 0;
  }
  return hash;
};

/**
 * Colour key of an activity type tag label, matched case-insensitively against the design labels.
 * Other labels get an extra palette key picked by a hash of the label; an empty label stays neutral.
 */
export const getActivityTypeTagColorKey = (label) => {
  const text = trimToString(label).toLowerCase();
  if (!text) {
    return NEUTRAL_TYPE_TAG_COLOR_KEY;
  }
  return TYPE_TAG_COLOR_KEYS[text] || EXTRA_TYPE_TAG_COLOR_KEYS[hashLabel(text) % EXTRA_TYPE_TAG_COLOR_KEYS.length];
};

/**
 * Label of a lesson's page types: the type when every page has the same one, Multi-Activity for several.
 *
 * @param {*} pages map of page counts keyed by activity type, e.g. { Sorting: 4 }
 * @returns the label, or an empty string when there are no countable pages
 */
const getPageTypeLabel = (pages) => {
  const types = Object.entries(pages || {})
    .filter(([type, count]) => trimToString(type) && type !== UNSUPPORTED_PAGE_TYPE && Number(count) > 0)
    .map(([type]) => trimToString(type));
  if (types.length === 0) {
    return '';
  }
  return types.length === 1 ? types[0] : MULTI_ACTIVITY_LABEL;
};

/**
 * Activity type tag of a lesson: the display text of its lessonType, then (classic activities only)
 * the label of its page types, falling back to its appName.
 *
 * @param {*} attributes lesson attributes
 * @returns { label, colorKey } or null when the lesson has none of these values
 */
export const getActivityTypeTag = (attributes) => {
  const { lessonType, appName, pages } = attributes || {};
  const label = getLessonTypeLabel(lessonType)
    || (isCourseActivity(attributes) ? '' : getPageTypeLabel(pages))
    || trimToString(appName);
  if (!label) {
    return null;
  }
  return { label, colorKey: getActivityTypeTagColorKey(label) };
};

const getLeadingNumber = (value) => {
  const match = trimToString(value).match(/^\s*(\d+)/);
  return match ? match[1] : '';
};

/**
 * Card title of a lesson. Course activities show "<unit> - <topic>" without the names' leading numbers
 * (the topic once only when it repeats the unit), and a unit summative assessment shows only its unit.
 * Other lessons show their name.
 *
 * @param {*} attributes lesson attributes
 * @returns the title text
 */
export const getActivityCardTitle = (attributes) => {
  const { name, unitName, topicName, lessonType } = attributes || {};
  if (isCourseActivity(attributes)) {
    const unit = stripLeadingNumber(unitName);
    const topic = stripLeadingNumber(topicName);
    // Some topics repeat their unit's name, e.g. "0 - Benchmark Assessment" / "1 - Benchmark Assessment"
    const showTopic = !isUnitSummativeAssessment(lessonType) && topic.toLowerCase() !== unit.toLowerCase();
    const title = [unit, showTopic ? topic : ''].filter((part) => part).join(TITLE_SEPARATOR);
    if (title) {
      return title;
    }
  }
  return name;
};

/**
 * Details line of a lesson, e.g. "Unit 2 • Topic 1 • Level 3". A unit summative assessment leaves out
 * the topic. Missing parts are left out.
 *
 * @param {*} attributes lesson attributes
 * @returns the details text, or an empty string when nothing is known
 */
export const getActivityDetailsLine = (attributes) => {
  const {
    topicName,
    unitName,
    lessonLevel,
    lessonType,
  } = attributes || {};
  const parts = [
    ['Unit', getLeadingNumber(unitName)],
    ['Topic', isUnitSummativeAssessment(lessonType) ? '' : getLeadingNumber(topicName)],
    ['Level', trimToString(lessonLevel)],
  ];
  return parts
    .filter(([, value]) => value)
    .map(([name, value]) => `${name} ${value}`)
    .join(DETAILS_SEPARATOR);
};

/**
 * Subject, grade and level line of a lesson, e.g. "English Language Arts • Grade 9-12 • Level 2".
 * Uses the first subject and the raw grade level. Missing parts are left out.
 *
 * @param {*} attributes lesson attributes
 * @returns the details text, or an empty string when nothing is known
 */
export const getActivitySubjectGradeLevelLine = (attributes) => {
  const { subjects, rawGradeLevel, lessonLevel } = attributes || {};
  const subject = Array.isArray(subjects) && subjects.length > 0 ? trimToString(subjects[0]) : '';
  const parts = [
    ['', subject],
    ['Grade ', trimToString(rawGradeLevel)],
    ['Level ', trimToString(lessonLevel)],
  ];
  return parts
    .filter(([, value]) => value)
    .map(([prefix, value]) => `${prefix}${value}`)
    .join(DETAILS_SEPARATOR);
};
